/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @octopusdeploy/custom-portal-rules/no-restricted-imports */

import { Permission } from "@octopusdeploy/octopus-server-client";
import { Environment } from "@octopusdeploy/utilities";
import cn from "classnames";
import * as React from "react";
import { useSelector } from "react-redux";
import type { RouteComponentProps } from "react-router";
import { withRouter } from "react-router";
import { client, repository, session } from "~/clientInstance";
import type { MenuNode } from "~/components/LinksMenu/MenuNode";
import ProjectSwitcher from "~/components/Navbar/ProjectSwitcher";
import { UserAccountMenu } from "~/components/Navbar/UserAccountMenu";
import Navigate from "~/components/Navigation/Navigate";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import PriorityNavigation from "~/components/PriorityNavigation/PriorityNavigation";
import SearchAndOpenPopover from "~/globalSearch/SearchAndOpenPopover";
import SimpleIconButton from "~/primitiveComponents/dataDisplay/IconButton/SimpleIconButton";
import routeLinks from "../../routeLinks";
import type { SpaceContext } from "../StandardLayout/SpaceLoader";
import { isSpaceNotFound, isSpecificSpaceContext } from "../StandardLayout/SpaceLoader";
import DevBanner from "./DevBanner";
import { NavigationBarUplift } from "./NavigationBarUplift";
import GitVariablesNotifications from "./NotificationPoller/GitVariablesNotifications";
import LicenceNotificationPoller from "./NotificationPoller/LicenceNotificationPoller";
import UpdateAvailableNotificationPoller from "./NotificationPoller/UpdateAvailableNotificationPoller";
import NotificationsMenu from "./NotificationsMenu";
import { SpacesMenu } from "./SpacesMenu/SpacesMenu";
import styles from "./style.module.less";

interface NavbarState {
    openProjectSwitcher: boolean;
    projectSwitcherAnchorEl: any;
    showDevBanner: boolean;
}

interface NavbarInnerProps extends RouteComponentProps<void> {
    spaceContext: SpaceContext;
    isNavigationVisualUpliftEnabled: boolean;
}

class NavbarInner extends React.Component<NavbarInnerProps, NavbarState> {
    constructor(props: NavbarInnerProps) {
        super(props);

        this.state = {
            openProjectSwitcher: false,
            projectSwitcherAnchorEl: null,
            showDevBanner: false,
        };
    }

    async componentDidMount() {
        this.setState({
            showDevBanner: Environment.isInDevelopmentMode(),
        });
    }

    render() {
        // The topnavbar id is used by E2E UI tests
        return (
            <Navigate
                render={(history) => (
                    <div id="topnavbar" className={styles.topnavbar}>
                        {this.state.showDevBanner ? <DevBanner onClose={() => this.setState({ showDevBanner: false })} /> : <></>}

                        <div className={cn(styles.navbar, this.props.isNavigationVisualUpliftEnabled ? styles.navbarUplift : undefined)}>
                            <SpacesMenu spaceContext={this.props.spaceContext} isNavigationVisualUpliftEnabled={this.props.isNavigationVisualUpliftEnabled} />

                            {this.props.isNavigationVisualUpliftEnabled ? (
                                <NavigationBarUplift items={this.getNavigationItems()} />
                            ) : (
                                <PriorityNavigation navigationItems={this.getNavigationItems()} maxNavigationItems={99} activeItemClassName={styles.navItemActive} className={styles.navbarCenter} showHamburgerIcon={true} />
                            )}

                            {isAllowed({ permission: Permission.ProjectView, wildcard: true }) && (
                                <ProjectSwitcher
                                    open={this.state.openProjectSwitcher}
                                    onRequestClose={(id, event) => {
                                        this.setState({ openProjectSwitcher: false });
                                        if (id && !event!.ctrlKey) {
                                            history.navigate(routeLinks.project(id).root);
                                        } else if (id) {
                                            history.open(routeLinks.project(id).root);
                                            if (event) {
                                                event.stopPropagation();
                                                event.preventDefault();
                                            }
                                        }
                                    }}
                                    anchorEl={this.state.projectSwitcherAnchorEl}
                                />
                            )}
                            <nav className={styles.navbarRight}>
                                <SearchAndOpenPopover />
                                <div className={styles.groupedRow}>
                                    <UpdateAvailableNotificationPoller />
                                    <LicenceNotificationPoller />
                                    <GitVariablesNotifications />
                                    <NotificationsMenu />
                                    <UserAccountMenu currentUser={session.currentUser!} spaceContext={this.props.spaceContext} />
                                </div>
                            </nav>
                        </div>
                    </div>
                )}
            />
        );
    }

    private getNavigationItems() {
        const navigationItems: MenuNode[] = [];

        if (isSpecificSpaceContext(this.props.spaceContext)) {
            navigationItems.push({ url: routeLinks.root, text: "Dashboard", exact: true });

            if (session.currentPermissions!.scopeToSpace(client.spaceId).hasPermissionInAnyScope(Permission.ProjectView)) {
                navigationItems.push({
                    url: routeLinks.projects.root,
                    text: "Projects",
                    accessoryView: (
                        <SimpleIconButton
                            className={styles.projectSwitcherLink}
                            onClick={(e) => {
                                e.preventDefault(); // Don't let this click event bubble up or we also select the Projects menu item.
                                this.toggleProjectSwitcher(e.currentTarget);
                            }}
                            disableRipple={true} // Our nav bar actions do not include ripple effects, so we want to keep this consistent.
                        >
                            <i className={`fa-solid fa-caret-down ${styles.dropdownIcon}`} />
                        </SimpleIconButton>
                    ),
                    linkClassName: styles.projectsLink,
                });
            }
            if (
                isAllowed({
                    permission: [Permission.MachineView, Permission.EnvironmentView, Permission.WorkerView],
                    wildcard: true,
                })
            ) {
                navigationItems.push({ url: routeLinks.infrastructure.root, text: "Infrastructure" });
            }
            if (session.currentPermissions!.scopeToSpace(client.spaceId).hasPermissionInAnyScope(Permission.TenantView)) {
                navigationItems.push({ url: routeLinks.tenants, text: "Tenants" });
            }
            if (session.currentPermissions!.scopeToSpace(client.spaceId).hasPermissionInAnyScope(Permission.InsightsReportView)) {
                navigationItems.push({ url: routeLinks.insights.reports, text: "Insights" });
            }
            if (
                isAllowed({
                    permission: [Permission.FeedView, Permission.LifecycleEdit, Permission.VariableView, Permission.ActionTemplateView, Permission.CertificateView],
                    wildcard: true,
                })
            ) {
                navigationItems.push({ url: routeLinks.library.root, text: "Library" });
            }
        }

        if (!isSpaceNotFound(this.props.spaceContext) || this.props.spaceContext.isAlsoInSystemContext) {
            const rootRouteLinks = isSpaceNotFound(this.props.spaceContext) ? routeLinks.withoutSpace() : routeLinks;
            if (session.currentPermissions!.hasPermissionInAnyScope(Permission.TaskView)) {
                navigationItems.push({ url: rootRouteLinks.tasks.rootForSpace(client.spaceId), text: "Tasks" });
            }
            if (
                session.currentPermissions!.scopeToSpace(repository.spaceId).hasAnyPermissions() ||
                isAllowed({
                    permission: [Permission.AdministerSystem, Permission.EventView, Permission.TeamView, Permission.UserView, Permission.UserRoleView, Permission.SpaceView, Permission.SpaceEdit, Permission.SpaceCreate, Permission.SpaceDelete],
                    wildcard: true,
                })
            ) {
                navigationItems.push({ url: rootRouteLinks.configuration.root, text: "Configuration" });
            }
        }

        return navigationItems;
    }

    private toggleProjectSwitcher = (e: HTMLElement) => {
        this.setState({
            openProjectSwitcher: !this.state.openProjectSwitcher,
            projectSwitcherAnchorEl: e,
        });
    };
}

interface NavbarProps extends RouteComponentProps<void> {
    spaceContext: SpaceContext;
}

function Navbar(props: NavbarProps) {
    const isNavigationVisualUpliftEnabled = useSelector((state: GlobalState) => state.configurationArea.features.isNavigationVisualUpliftEnabled);

    return <NavbarInner {...props} isNavigationVisualUpliftEnabled={isNavigationVisualUpliftEnabled} />;
}

// Unfortunately this component needs to be connected to the router purely so that it updates when the route changes.
// Otherwise, the connect() breaks all route-related updates.  This might be fixed in React 16.3 which should introduce the new context API
export default withRouter(Navbar);
